/**
 * Mock data for AR Demo Suite
 * All data is static for demo purposes
 */

// ============ Types ============

export interface DnBProfile {
  /** D&B Rating (e.g., "5A3" - financial strength + risk indicator) */
  rating: string;
  /** Paydex Score (1-100, measures payment performance, 80+ is good) */
  paydexScore: number;
  /** Failure Risk Score (1-9, 1 is lowest risk) */
  failureRiskScore: number;
  /** Failure Risk Class description */
  failureRiskClass: 'Low' | 'Moderate' | 'High';
  /** Delinquency Predictor Score (1-9, 1 is lowest risk) */
  delinquencyScore: number;
  /** Credit Limit Recommendation */
  creditLimitRecommendation: number;
  /** Years in business */
  yearsInBusiness: number;
  /** Number of employees */
  employees: number;
  /** Annual revenue */
  annualRevenue: number;
  /** Last updated date */
  lastUpdated: string;
}

export interface ARCustomer {
  id: string;
  name: string;
  accountNumber: string;
  dunsNumber: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  paymentTerms: string;
  totalOutstanding: number;
  pastDueAmount: number;
  pastDuePercentage: number;
  openInvoicesCount: number;
  lastContactDate: string;
  nextScheduledOutreach: string | null;
  status: 'needs_attention' | 'in_progress' | 'on_track';
  aging: {
    current: number;
    days30: number;
    days60: number;
    days90: number;
    over90: number;
  };
  /** D&B Business Credit Profile */
  dnbProfile?: DnBProfile;
}

export interface ARInvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface ARInvoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  amount: number;
  status: 'unpaid' | 'partial' | 'paid' | 'overdue';
  issueDate: string;
  dueDate: string;
  paymentTerms: string;
  items: ARInvoiceItem[];
}

export interface ARPayment {
  id: string;
  date: string;
  type: 'wire' | 'check' | 'ach';
  amount: number;
  reference: string;
  status: 'unmatched' | 'matched' | 'partial';
  matchedInvoiceId?: string;
  bankAccountId: 'chase-main' | 'bofa-ops';
}

export interface TranscriptLine {
  speaker: 'Customer' | 'Agent';
  text: string;
}

export interface PendingAction {
  id: string;
  type: 'approve_grace_period' | 'escalate' | 'request_info' | 'schedule_call';
  label: string;
  description: string;
  aiRecommended?: boolean;
  aiReason?: string;
}

export interface ARCommunication {
  id: string;
  customerId: string;
  type: 'email' | 'phone';
  direction: 'inbound' | 'outbound';
  date: string;
  subject?: string;
  details: string;
  response: string | null;
  emailBody?: string;
  contactPerson?: string;
  audioUrl?: string;
  audioDuration?: string;
  transcript?: TranscriptLine[];
  classifications: string[];
  sentiment: 'positive' | 'neutral' | 'negative' | null;
  summary?: string;
  aiDraftedResponse?: string;
  pendingActions?: PendingAction[];
}

export interface AISuggestedAction {
  id: string;
  customerId: string;
  type: 'send_reminder' | 'follow_up' | 'update_contact' | 'review_response';
  title: string;
  description: string;
  priority: 'urgent' | 'normal';
  relatedInvoices?: string[];
}

export interface CollectionTask {
  id: string;
  customerId: string;
  title: string;
  description: string;
  status: 'open' | 'completed' | 'closed';
  type: string;
  createdAt: string;
  completedAt?: string;
}

export interface ScheduledCommunication {
  id: string;
  customerId: string;
  scheduledDate: string;
  type: 'email' | 'phone';
  details: string;
}

export interface AIAgentRun {
  id: string;
  customerId: string;
  type: 'dispute_submission' | 'status_check' | 'portal_crawl';
  status: 'completed' | 'pending' | 'failed';
  startedAt: string;
  completedAt?: string;
  description: string;
  result?: string;
  portalUrl?: string;
  videoUrl?: string;
  invoiceNumber?: string;
  amountDisputed?: number;
}

export interface AIAgentMonitor {
  id: string;
  customerId: string;
  title: string;
  description: string;
  status: 'active' | 'paused' | 'completed';
  checkFrequency: string;
  lastCheckAt: string;
  nextCheckAt: string;
  checksLog: Array<{
    timestamp: string;
    status: 'no_update' | 'response_received' | 'error';
    message: string;
  }>;
}

// ============ Mock Customers ============
// Total overdue should approximately match MOCK_AR_OVERVIEW.totalOverdue ($51.07M)

export const MOCK_CUSTOMERS: ARCustomer[] = [
  {
    id: 'walmart',
    name: 'Walmart Inc.',
    accountNumber: 'WM-001001',
    dunsNumber: '05-131-9254',
    contactPerson: 'Jennifer Martinez',
    email: 'jmartinez@walmart.com',
    phone: '(479) 273-4000',
    address: '702 SW 8th Street, Bentonville, AR 72716',
    paymentTerms: 'Net 45',
    totalOutstanding: 18500000,
    pastDueAmount: 12400000,
    pastDuePercentage: 67.0,
    openInvoicesCount: 47,
    lastContactDate: '2026-01-20T14:30:00Z',
    nextScheduledOutreach: '2026-01-23T10:00:00Z',
    status: 'on_track',
    aging: {
      current: 6100000,
      days30: 5200000,
      days60: 4100000,
      days90: 2100000,
      over90: 1000000,
    },
    dnbProfile: {
      rating: '5A1',
      paydexScore: 80,
      failureRiskScore: 1,
      failureRiskClass: 'Low',
      delinquencyScore: 2,
      creditLimitRecommendation: 50000000,
      yearsInBusiness: 62,
      employees: 2100000,
      annualRevenue: 611000000000,
      lastUpdated: '2026-01-15',
    },
  },
  {
    id: 'target',
    name: 'Target Corporation',
    accountNumber: 'TG-002451',
    dunsNumber: '05-034-7891',
    contactPerson: 'Michael Chen',
    email: 'mchen@target.com',
    phone: '(612) 304-6073',
    address: '1000 Nicollet Mall, Minneapolis, MN 55403',
    paymentTerms: 'Net 30',
    totalOutstanding: 14200000,
    pastDueAmount: 8950000,
    pastDuePercentage: 63.0,
    openInvoicesCount: 32,
    lastContactDate: '2026-01-19T11:15:00Z',
    nextScheduledOutreach: '2026-01-24T14:00:00Z',
    status: 'needs_attention',
    aging: {
      current: 5250000,
      days30: 4100000,
      days60: 2850000,
      days90: 1200000,
      over90: 800000,
    },
    dnbProfile: {
      rating: '5A1',
      paydexScore: 82,
      failureRiskScore: 1,
      failureRiskClass: 'Low',
      delinquencyScore: 1,
      creditLimitRecommendation: 35000000,
      yearsInBusiness: 63,
      employees: 440000,
      annualRevenue: 109000000000,
      lastUpdated: '2026-01-18',
    },
  },
  {
    id: 'costco',
    name: 'Costco Wholesale',
    accountNumber: 'CC-003782',
    dunsNumber: '06-281-4532',
    contactPerson: 'Sarah Johnson',
    email: 'sjohnson@costco.com',
    phone: '(425) 313-8100',
    address: '999 Lake Drive, Issaquah, WA 98027',
    paymentTerms: 'Net 30',
    totalOutstanding: 11800000,
    pastDueAmount: 7200000,
    pastDuePercentage: 61.0,
    openInvoicesCount: 28,
    lastContactDate: '2026-01-18T09:45:00Z',
    nextScheduledOutreach: '2026-01-25T09:00:00Z',
    status: 'needs_attention',
    aging: {
      current: 4600000,
      days30: 3400000,
      days60: 2100000,
      days90: 1200000,
      over90: 500000,
    },
    dnbProfile: {
      rating: '5A1',
      paydexScore: 79,
      failureRiskScore: 1,
      failureRiskClass: 'Low',
      delinquencyScore: 2,
      creditLimitRecommendation: 40000000,
      yearsInBusiness: 48,
      employees: 316000,
      annualRevenue: 242000000000,
      lastUpdated: '2026-01-10',
    },
  },
  {
    id: 'kroger',
    name: 'The Kroger Co.',
    accountNumber: 'KR-004123',
    dunsNumber: '00-432-8765',
    contactPerson: 'David Williams',
    email: 'dwilliams@kroger.com',
    phone: '(513) 762-4000',
    address: '1014 Vine Street, Cincinnati, OH 45202',
    paymentTerms: 'Net 45',
    totalOutstanding: 9500000,
    pastDueAmount: 5100000,
    pastDuePercentage: 53.7,
    openInvoicesCount: 24,
    lastContactDate: '2026-01-17T16:20:00Z',
    nextScheduledOutreach: '2026-01-24T09:00:00Z',
    status: 'in_progress',
    aging: {
      current: 4400000,
      days30: 2800000,
      days60: 1500000,
      days90: 500000,
      over90: 300000,
    },
    dnbProfile: {
      rating: '5A2',
      paydexScore: 75,
      failureRiskScore: 2,
      failureRiskClass: 'Low',
      delinquencyScore: 3,
      creditLimitRecommendation: 25000000,
      yearsInBusiness: 141,
      employees: 430000,
      annualRevenue: 148000000000,
      lastUpdated: '2026-01-12',
    },
  },
  {
    id: 'whole-foods',
    name: 'Whole Foods Market',
    accountNumber: 'WF-005891',
    dunsNumber: '78-456-1234',
    contactPerson: 'Amanda Rodriguez',
    email: 'arodriguez@wholefoods.com',
    phone: '(512) 477-4455',
    address: '550 Bowie Street, Austin, TX 78703',
    paymentTerms: 'Net 30',
    totalOutstanding: 6800000,
    pastDueAmount: 2900000,
    pastDuePercentage: 42.6,
    openInvoicesCount: 18,
    lastContactDate: '2026-01-21T10:30:00Z',
    nextScheduledOutreach: '2026-01-28T10:00:00Z',
    status: 'on_track',
    aging: {
      current: 3900000,
      days30: 1800000,
      days60: 700000,
      days90: 300000,
      over90: 100000,
    },
    dnbProfile: {
      rating: '5A1',
      paydexScore: 81,
      failureRiskScore: 1,
      failureRiskClass: 'Low',
      delinquencyScore: 2,
      creditLimitRecommendation: 20000000,
      yearsInBusiness: 45,
      employees: 91000,
      annualRevenue: 21500000000,
      lastUpdated: '2026-01-14',
    },
  },
  {
    id: 'albertsons',
    name: 'Albertsons Companies',
    accountNumber: 'AB-006234',
    dunsNumber: '04-765-2341',
    contactPerson: 'Robert Thompson',
    email: 'rthompson@albertsons.com',
    phone: '(208) 395-6200',
    address: '250 Parkcenter Blvd, Boise, ID 83706',
    paymentTerms: 'Net 30',
    totalOutstanding: 4200000,
    pastDueAmount: 1600000,
    pastDuePercentage: 38.1,
    openInvoicesCount: 12,
    lastContactDate: '2026-01-20T15:00:00Z',
    nextScheduledOutreach: '2026-01-27T11:00:00Z',
    status: 'on_track',
    aging: {
      current: 2600000,
      days30: 1000000,
      days60: 400000,
      days90: 150000,
      over90: 50000,
    },
    dnbProfile: {
      rating: '4A2',
      paydexScore: 73,
      failureRiskScore: 2,
      failureRiskClass: 'Low',
      delinquencyScore: 3,
      creditLimitRecommendation: 15000000,
      yearsInBusiness: 85,
      employees: 290000,
      annualRevenue: 78000000000,
      lastUpdated: '2026-01-08',
    },
  },
  {
    id: 'publix',
    name: 'Publix Super Markets',
    accountNumber: 'PB-007512',
    dunsNumber: '08-123-4567',
    contactPerson: 'Lisa Anderson',
    email: 'landerson@publix.com',
    phone: '(863) 688-1188',
    address: '3300 Publix Corporate Pkwy, Lakeland, FL 33811',
    paymentTerms: 'Net 30',
    totalOutstanding: 5400000,
    pastDueAmount: 1850000,
    pastDuePercentage: 34.3,
    openInvoicesCount: 15,
    lastContactDate: '2026-01-19T13:45:00Z',
    nextScheduledOutreach: '2026-01-26T10:00:00Z',
    status: 'on_track',
    aging: {
      current: 3550000,
      days30: 1200000,
      days60: 450000,
      days90: 150000,
      over90: 50000,
    },
    dnbProfile: {
      rating: '5A1',
      paydexScore: 84,
      failureRiskScore: 1,
      failureRiskClass: 'Low',
      delinquencyScore: 1,
      creditLimitRecommendation: 18000000,
      yearsInBusiness: 94,
      employees: 255000,
      annualRevenue: 54000000000,
      lastUpdated: '2026-01-16',
    },
  },
  {
    id: 'cvs',
    name: 'CVS Health',
    accountNumber: 'CV-008234',
    dunsNumber: '04-567-8901',
    contactPerson: 'Thomas Wright',
    email: 'twright@cvshealth.com',
    phone: '(401) 765-1500',
    address: '1 CVS Drive, Woonsocket, RI 02895',
    paymentTerms: 'Net 45',
    totalOutstanding: 7200000,
    pastDueAmount: 3100000,
    pastDuePercentage: 43.1,
    openInvoicesCount: 21,
    lastContactDate: '2026-01-18T11:00:00Z',
    nextScheduledOutreach: '2026-01-23T15:30:00Z',
    status: 'in_progress',
    aging: {
      current: 4100000,
      days30: 1800000,
      days60: 900000,
      days90: 300000,
      over90: 100000,
    },
    dnbProfile: {
      rating: '5A2',
      paydexScore: 71,
      failureRiskScore: 3,
      failureRiskClass: 'Moderate',
      delinquencyScore: 4,
      creditLimitRecommendation: 22000000,
      yearsInBusiness: 61,
      employees: 300000,
      annualRevenue: 357000000000,
      lastUpdated: '2026-01-11',
    },
  },
  {
    id: 'walgreens',
    name: 'Walgreens Boots Alliance',
    accountNumber: 'WG-009876',
    dunsNumber: '05-234-5678',
    contactPerson: 'Karen Mitchell',
    email: 'kmitchell@walgreens.com',
    phone: '(847) 315-2500',
    address: '108 Wilmot Road, Deerfield, IL 60015',
    paymentTerms: 'Net 30',
    totalOutstanding: 6100000,
    pastDueAmount: 2650000,
    pastDuePercentage: 43.4,
    openInvoicesCount: 19,
    lastContactDate: '2026-01-20T09:15:00Z',
    nextScheduledOutreach: '2026-01-24T11:00:00Z',
    status: 'in_progress',
    aging: {
      current: 3450000,
      days30: 1500000,
      days60: 800000,
      days90: 250000,
      over90: 100000,
    },
    dnbProfile: {
      rating: '3A4',
      paydexScore: 52,
      failureRiskScore: 7,
      failureRiskClass: 'High',
      delinquencyScore: 5,
      creditLimitRecommendation: 12000000,
      yearsInBusiness: 124,
      employees: 325000,
      annualRevenue: 139000000000,
      lastUpdated: '2026-01-09',
    },
  },
  {
    id: 'heb',
    name: 'H-E-B Grocery',
    accountNumber: 'HB-010345',
    dunsNumber: '07-890-1234',
    contactPerson: 'Carlos Mendez',
    email: 'cmendez@heb.com',
    phone: '(210) 938-8000',
    address: '646 S Main Avenue, San Antonio, TX 78204',
    paymentTerms: 'Net 30',
    totalOutstanding: 4800000,
    pastDueAmount: 1450000,
    pastDuePercentage: 30.2,
    openInvoicesCount: 14,
    lastContactDate: '2026-01-21T08:30:00Z',
    nextScheduledOutreach: '2026-01-29T09:00:00Z',
    status: 'on_track',
    aging: {
      current: 3350000,
      days30: 950000,
      days60: 350000,
      days90: 100000,
      over90: 50000,
    },
    dnbProfile: {
      rating: '4A1',
      paydexScore: 83,
      failureRiskScore: 1,
      failureRiskClass: 'Low',
      delinquencyScore: 2,
      creditLimitRecommendation: 15000000,
      yearsInBusiness: 119,
      employees: 145000,
      annualRevenue: 43000000000,
      lastUpdated: '2026-01-17',
    },
  },
  {
    id: 'meijer',
    name: 'Meijer Inc.',
    accountNumber: 'MJ-011567',
    dunsNumber: '06-345-6789',
    contactPerson: 'Patricia Davis',
    email: 'pdavis@meijer.com',
    phone: '(616) 453-6711',
    address: '2929 Walker Ave NW, Grand Rapids, MI 49544',
    paymentTerms: 'Net 45',
    totalOutstanding: 3900000,
    pastDueAmount: 1200000,
    pastDuePercentage: 30.8,
    openInvoicesCount: 11,
    lastContactDate: '2026-01-17T14:00:00Z',
    nextScheduledOutreach: '2026-01-24T11:00:00Z',
    status: 'needs_attention',
    aging: {
      current: 2700000,
      days30: 800000,
      days60: 280000,
      days90: 80000,
      over90: 40000,
    },
    dnbProfile: {
      rating: '3A2',
      paydexScore: 72,
      failureRiskScore: 2,
      failureRiskClass: 'Low',
      delinquencyScore: 3,
      creditLimitRecommendation: 8000000,
      yearsInBusiness: 90,
      employees: 70000,
      annualRevenue: 23000000000,
      lastUpdated: '2026-01-13',
    },
  },
  {
    id: 'safeway',
    name: 'Safeway Inc.',
    accountNumber: 'SW-012789',
    dunsNumber: '03-456-7890',
    contactPerson: 'Brian Foster',
    email: 'bfoster@safeway.com',
    phone: '(925) 467-3000',
    address: '5918 Stoneridge Mall Road, Pleasanton, CA 94588',
    paymentTerms: 'Net 30',
    totalOutstanding: 3200000,
    pastDueAmount: 980000,
    pastDuePercentage: 30.6,
    openInvoicesCount: 9,
    lastContactDate: '2026-01-19T16:30:00Z',
    nextScheduledOutreach: '2026-01-30T10:00:00Z',
    status: 'needs_attention',
    aging: {
      current: 2220000,
      days30: 650000,
      days60: 230000,
      days90: 70000,
      over90: 30000,
    },
    dnbProfile: {
      rating: '4A2',
      paydexScore: 74,
      failureRiskScore: 2,
      failureRiskClass: 'Low',
      delinquencyScore: 3,
      creditLimitRecommendation: 12000000,
      yearsInBusiness: 99,
      employees: 250000,
      annualRevenue: 68000000000,
      lastUpdated: '2026-01-15',
    },
  },
  {
    id: 'trader-joes',
    name: "Trader Joe's",
    accountNumber: 'TJ-013456',
    dunsNumber: '09-567-8901',
    contactPerson: 'Emily Parker',
    email: 'eparker@traderjoes.com',
    phone: '(626) 599-3700',
    address: '800 S Shamrock Avenue, Monrovia, CA 91016',
    paymentTerms: 'Net 30',
    totalOutstanding: 2800000,
    pastDueAmount: 720000,
    pastDuePercentage: 25.7,
    openInvoicesCount: 8,
    lastContactDate: '2026-01-20T12:00:00Z',
    nextScheduledOutreach: '2026-01-31T14:00:00Z',
    status: 'needs_attention',
    aging: {
      current: 2080000,
      days30: 480000,
      days60: 160000,
      days90: 50000,
      over90: 30000,
    },
    dnbProfile: {
      rating: '3A1',
      paydexScore: 80,
      failureRiskScore: 1,
      failureRiskClass: 'Low',
      delinquencyScore: 2,
      creditLimitRecommendation: 10000000,
      yearsInBusiness: 57,
      employees: 50000,
      annualRevenue: 16500000000,
      lastUpdated: '2026-01-14',
    },
  },
  {
    id: 'aldi',
    name: 'ALDI Inc.',
    accountNumber: 'AL-014678',
    dunsNumber: '02-678-9012',
    contactPerson: 'Mark Sullivan',
    email: 'msullivan@aldi.us',
    phone: '(630) 879-8100',
    address: '1200 N Kirk Road, Batavia, IL 60510',
    paymentTerms: 'Net 30',
    totalOutstanding: 2400000,
    pastDueAmount: 550000,
    pastDuePercentage: 22.9,
    openInvoicesCount: 7,
    lastContactDate: '2026-01-18T10:45:00Z',
    nextScheduledOutreach: '2026-01-25T09:00:00Z',
    status: 'needs_attention',
    aging: {
      current: 1850000,
      days30: 380000,
      days60: 120000,
      days90: 30000,
      over90: 20000,
    },
    dnbProfile: {
      rating: '4A1',
      paydexScore: 78,
      failureRiskScore: 1,
      failureRiskClass: 'Low',
      delinquencyScore: 2,
      creditLimitRecommendation: 9000000,
      yearsInBusiness: 48,
      employees: 48000,
      annualRevenue: 17000000000,
      lastUpdated: '2026-01-12',
    },
  },
  {
    id: 'dollar-general',
    name: 'Dollar General',
    accountNumber: 'DG-015890',
    dunsNumber: '01-789-0123',
    contactPerson: 'Nancy Coleman',
    email: 'ncoleman@dollargeneral.com',
    phone: '(615) 855-4000',
    address: '100 Mission Ridge, Goodlettsville, TN 37072',
    paymentTerms: 'Net 45',
    totalOutstanding: 1900000,
    pastDueAmount: 420000,
    pastDuePercentage: 22.1,
    openInvoicesCount: 6,
    lastContactDate: '2026-01-21T11:15:00Z',
    nextScheduledOutreach: '2026-02-01T09:00:00Z',
    status: 'needs_attention',
    aging: {
      current: 1480000,
      days30: 290000,
      days60: 90000,
      days90: 25000,
      over90: 15000,
    },
    dnbProfile: {
      rating: '4A2',
      paydexScore: 76,
      failureRiskScore: 2,
      failureRiskClass: 'Low',
      delinquencyScore: 3,
      creditLimitRecommendation: 8000000,
      yearsInBusiness: 85,
      employees: 175000,
      annualRevenue: 38000000000,
      lastUpdated: '2026-01-10',
    },
  },
];

// ============ Mock Invoices ============

export const MOCK_INVOICES: ARInvoice[] = [
  {
    id: 'inv-001',
    invoiceNumber: 'INV-1749196174201',
    customerId: 'whole-foods',
    customerName: 'Whole Foods Market',
    amount: 56924.1,
    status: 'unpaid',
    issueDate: '2025-06-06',
    dueDate: '2025-07-05',
    paymentTerms: 'Net 30',
    items: [
      {
        description: 'Tomatoes',
        quantity: 121,
        unitPrice: 45.0,
        total: 5445.0,
      },
      { description: 'Carrots', quantity: 562, unitPrice: 2.7, total: 1517.4 },
      {
        description: 'Lettuce',
        quantity: 848,
        unitPrice: 18.0,
        total: 15264.0,
      },
      {
        description: 'Broccoli Florets',
        quantity: 458,
        unitPrice: 22.5,
        total: 10305.0,
      },
      { description: 'Spinach', quantity: 332, unitPrice: 22.5, total: 7470.0 },
    ],
  },
  {
    id: 'inv-002',
    invoiceNumber: 'INV-1749196161546',
    customerId: 'kroger',
    customerName: 'Kroger',
    amount: 40001.4,
    status: 'unpaid',
    issueDate: '2025-06-06',
    dueDate: '2025-07-05',
    paymentTerms: 'Net 30',
    items: [
      { description: 'Apples', quantity: 500, unitPrice: 35.0, total: 17500.0 },
      {
        description: 'Oranges',
        quantity: 400,
        unitPrice: 28.0,
        total: 11200.0,
      },
      {
        description: 'Bananas',
        quantity: 600,
        unitPrice: 18.84,
        total: 11301.4,
      },
    ],
  },
  {
    id: 'inv-003',
    invoiceNumber: 'INV-1749196149833',
    customerId: 'walmart',
    customerName: 'Walmart',
    amount: 31751.1,
    status: 'unpaid',
    issueDate: '2025-06-06',
    dueDate: '2025-07-05',
    paymentTerms: 'Net 30',
    items: [
      {
        description: 'Potatoes',
        quantity: 1200,
        unitPrice: 12.5,
        total: 15000.0,
      },
      { description: 'Onions', quantity: 800, unitPrice: 8.5, total: 6800.0 },
      { description: 'Garlic', quantity: 450, unitPrice: 22.11, total: 9951.1 },
    ],
  },
  {
    id: 'inv-004',
    invoiceNumber: 'INV-1749196138107',
    customerId: 'walmart',
    customerName: 'Walmart',
    amount: 64853.1,
    status: 'unpaid',
    issueDate: '2025-06-06',
    dueDate: '2025-07-05',
    paymentTerms: 'Net 30',
    items: [
      {
        description: 'Bell Peppers',
        quantity: 750,
        unitPrice: 42.0,
        total: 31500.0,
      },
      {
        description: 'Cucumbers',
        quantity: 900,
        unitPrice: 18.5,
        total: 16650.0,
      },
      {
        description: 'Zucchini',
        quantity: 680,
        unitPrice: 24.56,
        total: 16703.1,
      },
    ],
  },
  {
    id: 'inv-005',
    invoiceNumber: 'INV-1749196126399',
    customerId: 'costco',
    customerName: 'Costco',
    amount: 77841.9,
    status: 'unpaid',
    issueDate: '2025-06-06',
    dueDate: '2025-07-05',
    paymentTerms: 'Net 30',
    items: [
      {
        description: 'Mixed Greens',
        quantity: 1500,
        unitPrice: 28.0,
        total: 42000.0,
      },
      {
        description: 'Cherry Tomatoes',
        quantity: 890,
        unitPrice: 40.27,
        total: 35841.9,
      },
    ],
  },
  {
    id: 'inv-006',
    invoiceNumber: 'INV-1749196114393',
    customerId: 'costco',
    customerName: 'Costco',
    amount: 69619.5,
    status: 'unpaid',
    issueDate: '2025-06-06',
    dueDate: '2025-07-05',
    paymentTerms: 'Net 30',
    items: [
      {
        description: 'Avocados',
        quantity: 2000,
        unitPrice: 22.5,
        total: 45000.0,
      },
      {
        description: 'Limes',
        quantity: 1500,
        unitPrice: 16.41,
        total: 24619.5,
      },
    ],
  },
  {
    id: 'inv-007',
    invoiceNumber: 'INV-1749196102392',
    customerId: 'walmart',
    customerName: 'Walmart',
    amount: 78209.1,
    status: 'unpaid',
    issueDate: '2025-06-06',
    dueDate: '2025-07-05',
    paymentTerms: 'Net 30',
    items: [
      {
        description: 'Strawberries',
        quantity: 1200,
        unitPrice: 38.0,
        total: 45600.0,
      },
      {
        description: 'Blueberries',
        quantity: 650,
        unitPrice: 50.17,
        total: 32609.1,
      },
    ],
  },
  {
    id: 'inv-008',
    invoiceNumber: 'INV-1749196090317',
    customerId: 'costco',
    customerName: 'Costco',
    amount: 87232.5,
    status: 'unpaid',
    issueDate: '2025-06-06',
    dueDate: '2025-07-05',
    paymentTerms: 'Net 30',
    items: [
      {
        description: 'Mushrooms',
        quantity: 1800,
        unitPrice: 32.0,
        total: 57600.0,
      },
      {
        description: 'Asparagus',
        quantity: 550,
        unitPrice: 53.88,
        total: 29632.5,
      },
    ],
  },
  {
    id: 'inv-009',
    invoiceNumber: 'INV-1749196078009',
    customerId: 'walmart',
    customerName: 'Walmart',
    amount: 52323.3,
    status: 'unpaid',
    issueDate: '2025-06-06',
    dueDate: '2025-07-05',
    paymentTerms: 'Net 30',
    items: [
      {
        description: 'Sweet Corn',
        quantity: 2200,
        unitPrice: 15.0,
        total: 33000.0,
      },
      {
        description: 'Green Beans',
        quantity: 850,
        unitPrice: 22.73,
        total: 19323.3,
      },
    ],
  },
  {
    id: 'inv-010',
    invoiceNumber: 'INV-1749196066121',
    customerId: 'publix',
    customerName: 'Publix',
    amount: 74898.9,
    status: 'unpaid',
    issueDate: '2025-06-06',
    dueDate: '2025-07-05',
    paymentTerms: 'Net 30',
    items: [
      { description: 'Kale', quantity: 1100, unitPrice: 35.0, total: 38500.0 },
      {
        description: 'Swiss Chard',
        quantity: 920,
        unitPrice: 39.56,
        total: 36398.9,
      },
    ],
  },
];

// ============ Mock Payments (for Reconciliation) ============

export const MOCK_PAYMENTS: ARPayment[] = [
  // Chase Business Checking payments
  {
    id: 'pmt-001',
    date: '2026-01-20',
    type: 'wire',
    amount: 1250000.0,
    reference: 'WIRE-WM-2026-0120',
    status: 'unmatched',
    bankAccountId: 'chase-main',
  },
  {
    id: 'pmt-002',
    date: '2026-01-19',
    type: 'ach',
    amount: 847500.0,
    reference: 'ACH-TGT-2026-0119',
    status: 'unmatched',
    bankAccountId: 'chase-main',
  },
  {
    id: 'pmt-003',
    date: '2026-01-18',
    type: 'wire',
    amount: 523000.0,
    reference: 'WIRE-COST-2026-0118',
    status: 'unmatched',
    bankAccountId: 'chase-main',
  },
  {
    id: 'pmt-004',
    date: '2026-01-17',
    type: 'check',
    amount: 312500.0,
    reference: 'CHK-KRO-78234',
    status: 'unmatched',
    bankAccountId: 'chase-main',
  },
  // Bank of America Operations payments
  {
    id: 'pmt-005',
    date: '2026-01-20',
    type: 'ach',
    amount: 425000.0,
    reference: 'ACH-WFM-2026-0120',
    status: 'unmatched',
    bankAccountId: 'bofa-ops',
  },
  {
    id: 'pmt-006',
    date: '2026-01-19',
    type: 'wire',
    amount: 198750.0,
    reference: 'WIRE-ALB-2026-0119',
    status: 'unmatched',
    bankAccountId: 'bofa-ops',
  },
  {
    id: 'pmt-007',
    date: '2026-01-18',
    type: 'check',
    amount: 675000.0,
    reference: 'CHK-PBX-45678',
    status: 'unmatched',
    bankAccountId: 'bofa-ops',
  },
  {
    id: 'pmt-008',
    date: '2026-01-17',
    type: 'ach',
    amount: 89500.0,
    reference: 'ACH-CVS-2026-0117',
    status: 'unmatched',
    bankAccountId: 'bofa-ops',
  },
  {
    id: 'pmt-009',
    date: '2026-01-16',
    type: 'wire',
    amount: 445000.0,
    reference: 'WIRE-HEB-2026-0116',
    status: 'unmatched',
    bankAccountId: 'bofa-ops',
  },
  // Multi-invoice payment - covers two ALDI invoices
  {
    id: 'pmt-010',
    date: '2026-01-15',
    type: 'wire',
    amount: 342000.0, // Covers oi-013 ($187,000) + oi-014 ($155,000)
    reference: 'WIRE-ALDI-BATCH-0115',
    status: 'unmatched',
    bankAccountId: 'chase-main',
  },
  // Partial payment - only covers part of Dollar General invoice
  {
    id: 'pmt-011',
    date: '2026-01-14',
    type: 'ach',
    amount: 125000.0, // Partial payment on $245,000 invoice
    reference: 'ACH-DG-PARTIAL-0114',
    status: 'unmatched',
    bankAccountId: 'bofa-ops',
  },
];

// Outstanding invoices for reconciliation matching
export interface ReconciliationInvoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  customerId: string;
  amount: number;
  amountPaid?: number; // For partial payments
  dueDate: string;
  status: 'unpaid' | 'paid' | 'partial';
}

export const MOCK_OUTSTANDING_INVOICES: ReconciliationInvoice[] = [
  {
    id: 'oi-001',
    invoiceNumber: 'WM-2026-0892',
    customerName: 'Walmart Inc.',
    customerId: 'walmart',
    amount: 1250000.0,
    dueDate: '2026-01-25',
    status: 'unpaid',
  },
  {
    id: 'oi-002',
    invoiceNumber: 'TG-2026-0456',
    customerName: 'Target Corporation',
    customerId: 'target',
    amount: 847500.0,
    dueDate: '2026-01-22',
    status: 'unpaid',
  },
  {
    id: 'oi-003',
    invoiceNumber: 'CC-2026-0234',
    customerName: 'Costco Wholesale',
    customerId: 'costco',
    amount: 523000.0,
    dueDate: '2026-01-20',
    status: 'unpaid',
  },
  {
    id: 'oi-004',
    invoiceNumber: 'KR-2026-0178',
    customerName: 'The Kroger Co.',
    customerId: 'kroger',
    amount: 312500.0,
    dueDate: '2026-01-18',
    status: 'unpaid',
  },
  {
    id: 'oi-005',
    invoiceNumber: 'WF-2026-0089',
    customerName: 'Whole Foods Market',
    customerId: 'whole-foods',
    amount: 425000.0,
    dueDate: '2026-01-24',
    status: 'unpaid',
  },
  {
    id: 'oi-006',
    invoiceNumber: 'AB-2026-0345',
    customerName: 'Albertsons Companies',
    customerId: 'albertsons',
    amount: 198750.0,
    dueDate: '2026-01-21',
    status: 'unpaid',
  },
  {
    id: 'oi-007',
    invoiceNumber: 'PX-2026-0567',
    customerName: 'Publix Super Markets',
    customerId: 'publix',
    amount: 675000.0,
    dueDate: '2026-01-19',
    status: 'unpaid',
  },
  {
    id: 'oi-008',
    invoiceNumber: 'CV-2026-0123',
    customerName: 'CVS Health',
    customerId: 'cvs',
    amount: 89500.0,
    dueDate: '2026-01-17',
    status: 'unpaid',
  },
  {
    id: 'oi-009',
    invoiceNumber: 'HB-2026-0789',
    customerName: 'H-E-B Grocery',
    customerId: 'heb',
    amount: 445000.0,
    dueDate: '2026-01-16',
    status: 'unpaid',
  },
  {
    id: 'oi-010',
    invoiceNumber: 'WG-2026-0234',
    customerName: 'Walgreens Boots Alliance',
    customerId: 'walgreens',
    amount: 156000.0,
    dueDate: '2026-01-23',
    status: 'unpaid',
  },
  {
    id: 'oi-011',
    invoiceNumber: 'MJ-2026-0456',
    customerName: 'Meijer Inc.',
    customerId: 'meijer',
    amount: 287500.0,
    dueDate: '2026-01-20',
    status: 'unpaid',
  },
  {
    id: 'oi-012',
    invoiceNumber: 'SF-2026-0678',
    customerName: 'Safeway Inc.',
    customerId: 'safeway',
    amount: 334000.0,
    dueDate: '2026-01-22',
    status: 'unpaid',
  },
  // Two ALDI invoices that will be covered by one payment (pmt-010)
  {
    id: 'oi-013',
    invoiceNumber: 'AL-2026-0891',
    customerName: 'ALDI Inc.',
    customerId: 'aldi',
    amount: 187000.0,
    dueDate: '2026-01-18',
    status: 'unpaid',
  },
  {
    id: 'oi-014',
    invoiceNumber: 'AL-2026-0892',
    customerName: 'ALDI Inc.',
    customerId: 'aldi',
    amount: 155000.0,
    dueDate: '2026-01-19',
    status: 'unpaid',
  },
  // Dollar General invoice with partial payment scenario (pmt-011 covers $125k of $245k)
  {
    id: 'oi-015',
    invoiceNumber: 'DG-2026-0445',
    customerName: 'Dollar General',
    customerId: 'dollar-general',
    amount: 245000.0,
    dueDate: '2026-01-15',
    status: 'unpaid',
  },
];

// Pre-computed matches for demo
export const MOCK_POTENTIAL_MATCHES = [
  {
    paymentId: 'pmt-001',
    invoiceId: 'oi-001',
    confidence: 98,
    reasons: ['Exact amount match', 'Reference contains customer code'],
  },
  {
    paymentId: 'pmt-002',
    invoiceId: 'oi-002',
    confidence: 100,
    reasons: ['Exact amount match', 'Date within 3 days'],
  },
  {
    paymentId: 'pmt-003',
    invoiceId: 'oi-003',
    confidence: 95,
    reasons: ['Exact amount match', 'Reference pattern match'],
  },
  {
    paymentId: 'pmt-004',
    invoiceId: 'oi-004',
    confidence: 92,
    reasons: ['Exact amount match'],
  },
  {
    paymentId: 'pmt-005',
    invoiceId: 'oi-005',
    confidence: 97,
    reasons: ['Exact amount match', 'Customer ACH pattern'],
  },
  {
    paymentId: 'pmt-006',
    invoiceId: 'oi-006',
    confidence: 94,
    reasons: ['Exact amount match'],
  },
  {
    paymentId: 'pmt-007',
    invoiceId: 'oi-007',
    confidence: 96,
    reasons: ['Exact amount match', 'Check reference match'],
  },
  {
    paymentId: 'pmt-008',
    invoiceId: 'oi-008',
    confidence: 100,
    reasons: ['Exact amount match', 'ACH customer ID match'],
  },
  {
    paymentId: 'pmt-009',
    invoiceId: 'oi-009',
    confidence: 93,
    reasons: ['Exact amount match'],
  },
  // Multi-invoice match: one payment covers two ALDI invoices
  {
    paymentId: 'pmt-010',
    invoiceId: 'oi-013',
    confidence: 96,
    reasons: [
      'Batch payment detected',
      'Customer reference match',
      'Sum equals invoice total',
    ],
    isMultiInvoice: true,
  },
  {
    paymentId: 'pmt-010',
    invoiceId: 'oi-014',
    confidence: 96,
    reasons: [
      'Batch payment detected',
      'Customer reference match',
      'Sum equals invoice total',
    ],
    isMultiInvoice: true,
  },
  // Partial payment match: payment covers part of Dollar General invoice
  {
    paymentId: 'pmt-011',
    invoiceId: 'oi-015',
    confidence: 85,
    reasons: [
      'Partial payment',
      'Customer ACH pattern',
      '$120,000 remaining balance',
    ],
    isPartialPayment: true,
    partialAmount: 125000.0,
  },
];

// ============ Mock Communications ============

export const MOCK_COMMUNICATIONS: ARCommunication[] = [
  {
    id: 'comm-001',
    customerId: 'walmart',
    type: 'email',
    direction: 'outbound',
    date: '2026-01-01T19:00:00',
    subject: 'Invoice Batch #WM-2026-Q1 Payment Reminder',
    details: 'Invoice Batch #WM-2026-Q1 Payment Reminder',
    response: 'Will review with AP team',
    classifications: ['Payment Reminder'],
    sentiment: null,
    summary: 'Sent payment reminder for 12 overdue invoices totaling $4.2M',
    emailBody: `Dear Jennifer Martinez,

I hope this email finds you well. I'm writing to remind you about the outstanding payment for Invoice Batch #WM-2026-Q1 totaling $4,200,000, which includes 12 invoices now past due.

As per our records, these payments are now 15-45 days past due. We value our partnership with Walmart and would appreciate your prompt attention to this matter.

If you have already processed these payments, please share payment confirmation details. If not, please arrange for payment at your earliest convenience.

For your reference, I've attached a detailed breakdown of all outstanding invoices. If you have any questions or need assistance, please don't hesitate to contact our accounts receivable department.

Thank you for your attention to this matter.

Best regards,
Financial Team
Gently AI`,
  },
  {
    id: 'comm-002',
    customerId: 'walmart',
    type: 'phone',
    direction: 'outbound',
    date: '2026-01-06T19:00:00',
    details: 'Follow up on email',
    response: 'Will escalate to finance director',
    contactPerson: 'Jennifer Martinez',
    audioUrl: '/mock-audio/call-001.mp3',
    audioDuration: '2:40',
    classifications: ['Payment Promise', 'Escalation'],
    sentiment: 'positive',
    summary:
      'Customer confirmed they are escalating to finance director and expects partial payment of $5M within the next week.',
    transcript: [
      { speaker: 'Customer', text: 'Hello?' },
      {
        speaker: 'Agent',
        text: 'Hello, Jennifer. This is Riley from Gently Foods. How are you today?',
      },
      { speaker: 'Customer', text: "Hey. I'm good. How are you doing?" },
      {
        speaker: 'Agent',
        text: "I'm doing well. Thank you for asking. I'm reaching out regarding an important matter with your account. We received your latest purchase order, but unfortunately, we can't process it at the moment because your account is above the credit limit. Currently, the total amount past due is $12.4 million. Specifically, I wanted to discuss the Q1 invoice batch, which totals $4.2 million and is overdue. Do you need any information or clarification regarding these invoices?",
      },
      {
        speaker: 'Customer',
        text: "I see. Yeah, we've been having some internal delays with our new ERP system. Let me escalate this to our finance director and we'll get back to you.",
      },
      {
        speaker: 'Agent',
        text: "Thank you for looking into that. I understand system transitions can cause delays. Is there a specific timeline you're anticipating for resolution?",
      },
      {
        speaker: 'Customer',
        text: 'We should be able to process about $5 million by next week, then the rest within 30 days.',
      },
      {
        speaker: 'Agent',
        text: "Alright. So we can expect $5 million within the next week, with the remainder within 30 days. That's great to hear. I'll make a note of that. Is there anything else I can assist you with today?",
      },
      { speaker: 'Customer', text: "No. That's it. Thanks." },
      {
        speaker: 'Agent',
        text: 'Thank you for your time, Jennifer. We appreciate your business. Have a great day.',
      },
    ],
    aiDraftedResponse: `Hi Jennifer,

Thank you for speaking with us today. As discussed, we understand you're escalating the outstanding balance to your finance director and expect to process approximately $5 million within the next week, with the remaining balance within 30 days.

We'll follow up next week to confirm the payment has been processed.

Best regards,
Financial Team`,
  },
  {
    id: 'comm-003',
    customerId: 'walmart',
    type: 'email',
    direction: 'inbound',
    date: '2026-01-08T10:30:00',
    subject: 'Re: Invoice Batch #WM-2026-Q1 Payment Reminder',
    details: 'Customer response about payment',
    response: 'Acknowledged',
    classifications: ['Payment Promise'],
    sentiment: 'positive',
    summary:
      'Customer confirmed $5.2M wire transfer scheduled for Friday, remaining balance by month end.',
    emailBody: `Hi,

I spoke with our finance director and we have scheduled a wire transfer for $5,200,000 to be processed this Friday.

The remaining balance of $7.2M will be processed by end of month once our new payment system is fully online.

Can you send me the updated invoice summary and confirm the wire details?

Thanks,
Jennifer`,
    aiDraftedResponse: `Hi Jennifer,

Thank you for the update. We're pleased to hear the $5.2M wire transfer is scheduled for Friday.

I've attached the updated invoice summary as requested. Please use the following wire details:

Bank: First National Bank
Account: XXXX-XXXX-1234
Routing: 021000089

We'll monitor for the payment and follow up regarding the remaining $7.2M balance at month end.

Best regards,
Financial Team`,
  },
  {
    id: 'comm-004',
    customerId: 'target',
    type: 'email',
    direction: 'outbound',
    date: '2026-01-15T14:00:00',
    subject: 'Payment Status Update Request - Q4 Invoices',
    details: 'Checking on outstanding invoices',
    response: 'No response yet',
    classifications: ['Payment Inquiry'],
    sentiment: null,
    summary:
      'Requested update on 32 outstanding invoices totaling $8.95M overdue.',
    emailBody: `Dear Michael,

Hope you're doing well. I wanted to check in regarding the outstanding balance on your account.

We currently show 32 invoices with a combined overdue amount of $8,950,000. Would you be able to provide an update on when we might expect payment?

Happy to discuss payment arrangements if needed.

Best regards,
Financial Team`,
  },
  // Additional Target communications history
  {
    id: 'comm-target-002',
    customerId: 'target',
    type: 'phone',
    direction: 'outbound',
    date: '2026-01-14T10:30:00',
    subject: 'Call with AP Team',
    details: 'Discussed Q4 invoice backlog',
    response: 'Promised review by end of week',
    classifications: ['Payment Discussion'],
    sentiment: 'neutral',
    summary:
      'Spoke with Target AP representative about backlog. They acknowledged the invoices and committed to internal review.',
  },
  {
    id: 'comm-target-003',
    customerId: 'target',
    type: 'email',
    direction: 'inbound',
    date: '2026-01-12T16:45:00',
    subject: 'Re: Invoice Batch TG-2025-Q4 - Partial Payment Notice',
    details: 'Partial payment notification received',
    response: 'Acknowledged',
    classifications: ['Payment Update'],
    sentiment: 'positive',
    summary:
      'Target confirmed partial payment of $2.1M processed, remaining balance under review.',
    emailBody: `Hi,

This is to confirm we have processed a partial payment of $2,100,000 toward the outstanding Q4 invoices. 

The remaining balance is currently under review by our merchandising team as there are some quantity discrepancies we need to verify against our receiving records.

We expect to have an update within the next 7-10 business days.

Regards,
Michael Chen
Target Accounts Payable`,
  },
  {
    id: 'comm-target-004',
    customerId: 'target',
    type: 'email',
    direction: 'outbound',
    date: '2026-01-10T09:00:00',
    subject: 'Invoice Batch TG-2025-Q4 - Payment Follow-up',
    details: 'Following up on December invoices',
    response: null,
    classifications: ['Payment Reminder'],
    sentiment: null,
    summary: 'Standard payment follow-up for Q4 invoice batch totaling $11.2M.',
    emailBody: `Dear Michael,

I hope this email finds you well. I'm reaching out regarding Invoice Batch TG-2025-Q4, which includes 45 invoices totaling $11,200,000.

These invoices are now past due per our Net 30 terms. Could you please provide an update on the payment timeline?

Please let me know if you need any documentation or have questions about specific invoices.

Best regards,
Financial Team`,
  },
  {
    id: 'comm-target-005',
    customerId: 'target',
    type: 'phone',
    direction: 'inbound',
    date: '2026-01-08T14:20:00',
    subject: 'Inbound call from Target AP',
    details: 'Michael called to discuss disputed invoice',
    response: 'Escalated to disputes team',
    classifications: ['Dispute', 'Invoice Query'],
    sentiment: 'negative',
    summary:
      'Target disputing Invoice #TG-2025-4521 ($847,000) due to alleged short shipment on organic produce.',
  },
  {
    id: 'comm-target-006',
    customerId: 'target',
    type: 'email',
    direction: 'outbound',
    date: '2026-01-05T11:00:00',
    subject: 'December 2025 Statement - Target Corporation',
    details: 'Monthly statement sent',
    response: null,
    classifications: ['Statement'],
    sentiment: null,
    summary:
      'December statement showing total balance of $14.2M, with $8.95M overdue.',
    emailBody: `Dear Target Accounts Payable Team,

Please find attached your December 2025 account statement.

Summary:
- Current Balance: $5,250,000
- 1-30 Days Past Due: $4,100,000
- 31-60 Days Past Due: $2,850,000
- 61-90 Days Past Due: $1,200,000
- Over 90 Days: $800,000
- Total Outstanding: $14,200,000

Please review and let us know if you have any questions.

Best regards,
Financial Team`,
  },
  {
    id: 'comm-target-007',
    customerId: 'target',
    type: 'phone',
    direction: 'outbound',
    date: '2025-12-28T15:30:00',
    subject: 'Year-end payment discussion',
    details: 'Called to discuss year-end payments',
    response: 'Will process in January',
    classifications: ['Payment Discussion'],
    sentiment: 'neutral',
    summary:
      'Target indicated year-end freeze on payments. Will resume processing in early January.',
  },
  {
    id: 'comm-target-008',
    customerId: 'target',
    type: 'email',
    direction: 'inbound',
    date: '2025-12-20T10:15:00',
    subject: 'Re: Outstanding Balance - Holiday Schedule',
    details: 'Payment delay notification',
    response: 'Acknowledged',
    classifications: ['Payment Update'],
    sentiment: 'neutral',
    summary:
      'Target notified of reduced AP staffing through holidays, payments delayed until January.',
    emailBody: `Hi,

Due to the holiday schedule, our Accounts Payable department will be operating with reduced staff from December 21st through January 2nd.

All pending payments will be processed in early January. We appreciate your patience during this time.

Best,
Michael Chen`,
  },
  {
    id: 'comm-target-009',
    customerId: 'target',
    type: 'email',
    direction: 'outbound',
    date: '2025-12-15T09:30:00',
    subject: 'November 2025 Statement - Target Corporation',
    details: 'Monthly statement sent',
    response: null,
    classifications: ['Statement'],
    sentiment: null,
    summary: 'November statement sent showing $12.8M total outstanding.',
  },
  {
    id: 'comm-target-010',
    customerId: 'target',
    type: 'phone',
    direction: 'outbound',
    date: '2025-12-10T11:00:00',
    subject: 'Payment timeline discussion',
    details: 'Follow-up call on November invoices',
    response: 'Check cut by end of month',
    classifications: ['Payment Discussion'],
    sentiment: 'positive',
    summary:
      'Target confirmed $3.2M payment batch scheduled for December 18th.',
  },
  {
    id: 'comm-target-011',
    customerId: 'target',
    type: 'email',
    direction: 'inbound',
    date: '2025-12-05T14:00:00',
    subject: 'Payment Confirmation - Wire Transfer',
    details: 'Payment received confirmation',
    response: 'Confirmed receipt',
    classifications: ['Payment Confirmation'],
    sentiment: 'positive',
    summary:
      'Target confirmed wire transfer of $4.5M received and applied to oldest invoices.',
    emailBody: `Hello,

This email confirms that wire transfer #TGT-WT-120525 in the amount of $4,500,000 has been processed.

Please apply this payment to the following invoices (oldest first):
- TG-2025-3892: $1,200,000
- TG-2025-3893: $890,000
- TG-2025-3894: $1,100,000
- TG-2025-3895: $780,000
- TG-2025-3896: $530,000

Regards,
Target AP Team`,
  },
  {
    id: 'comm-target-012',
    customerId: 'target',
    type: 'phone',
    direction: 'outbound',
    date: '2025-11-28T10:00:00',
    subject: 'Quarterly review call',
    details: 'Q4 planning discussion',
    response: 'Good standing confirmed',
    classifications: ['Account Review'],
    sentiment: 'positive',
    summary:
      'Quarterly business review completed. Target confirmed strong demand for Q1 2026, discussed credit limit increase.',
  },
  {
    id: 'comm-target-013',
    customerId: 'target',
    type: 'email',
    direction: 'outbound',
    date: '2025-11-20T09:00:00',
    subject: 'Past Due Notice - Action Required',
    details: 'Formal past due notice sent',
    response: 'Under review',
    classifications: ['Past Due Notice'],
    sentiment: null,
    summary:
      'Formal past due notice for invoices 60+ days overdue totaling $2.8M.',
    emailBody: `Dear Target Accounts Payable,

This is a formal notice that the following invoices are now significantly past due:

61-90 Days Past Due: $1,800,000 (12 invoices)
Over 90 Days Past Due: $1,000,000 (5 invoices)

Immediate attention is required to avoid potential credit holds on future orders.

Please contact us within 5 business days to discuss payment arrangements.

Regards,
Financial Team`,
  },
  {
    id: 'comm-target-014',
    customerId: 'target',
    type: 'email',
    direction: 'inbound',
    date: '2025-11-15T16:30:00',
    subject: 'Re: Invoice TG-2025-3654 Query',
    details: 'Invoice clarification received',
    response: 'Resolved',
    classifications: ['Invoice Query'],
    sentiment: 'neutral',
    summary:
      'Target requested POD for invoice TG-2025-3654. Documentation provided and issue resolved.',
    emailBody: `Hi,

We need proof of delivery for Invoice TG-2025-3654 ($245,000) before we can process payment. Our receiving records show a discrepancy.

Please provide the signed BOL at your earliest convenience.

Thanks,
Michael`,
  },
  {
    id: 'comm-target-015',
    customerId: 'target',
    type: 'phone',
    direction: 'inbound',
    date: '2025-11-08T09:45:00',
    subject: 'Pricing discussion',
    details: 'Target requested volume discount discussion',
    response: 'Scheduled follow-up meeting',
    classifications: ['Pricing Discussion'],
    sentiment: 'positive',
    summary:
      'Target interested in negotiating volume-based pricing for Q1 2026 based on projected 15% order increase.',
  },
  {
    id: 'comm-target-016',
    customerId: 'target',
    type: 'email',
    direction: 'outbound',
    date: '2025-11-01T10:00:00',
    subject: 'October 2025 Statement - Target Corporation',
    details: 'Monthly statement sent',
    response: 'Acknowledged',
    classifications: ['Statement'],
    sentiment: null,
    summary: 'October statement sent showing $10.5M outstanding balance.',
  },
  {
    id: 'comm-target-017',
    customerId: 'target',
    type: 'email',
    direction: 'inbound',
    date: '2025-10-25T11:20:00',
    subject: 'Re: Credit Application Increase',
    details: 'Credit increase approved',
    response: 'Updated in system',
    classifications: ['Credit Update'],
    sentiment: 'positive',
    summary:
      'Target approved for credit limit increase from $30M to $35M based on payment history.',
    emailBody: `Hello,

Please be advised that we have approved the requested credit limit increase for your account with Target Corporation.

New Credit Limit: $35,000,000
Effective Date: October 25, 2025

Thank you for your continued partnership.

Best regards,
Target Credit Department`,
  },
  {
    id: 'comm-target-018',
    customerId: 'target',
    type: 'phone',
    direction: 'outbound',
    date: '2025-10-18T14:30:00',
    subject: 'Payment confirmation call',
    details: 'Confirmed receipt of October payment',
    response: 'Confirmed',
    classifications: ['Payment Confirmation'],
    sentiment: 'positive',
    summary: 'Confirmed receipt of $5.8M payment. Account in good standing.',
  },
  {
    id: 'comm-005',
    customerId: 'costco',
    type: 'email',
    direction: 'inbound',
    date: '2026-01-19T09:15:00',
    subject: 'Re: Outstanding Balance Inquiry',
    details: 'Customer dispute on pricing',
    response: 'Under review',
    classifications: ['Dispute', 'Pricing Issue'],
    sentiment: 'negative',
    summary:
      'Customer disputing $1.2M in charges due to pricing discrepancy on produce shipments.',
    emailBody: `Hi,

We've reviewed the invoices you sent and found pricing discrepancies on several produce shipments from December. 

The contracted rate for organic tomatoes was $2.45/lb but we were billed at $2.89/lb across 15 shipments. This accounts for approximately $1.2M in overcharges.

We're withholding payment on these invoices pending resolution. Please have your pricing team review and issue corrected invoices.

Regards,
Sarah Johnson
Accounts Payable, Costco`,
  },
  // Walgreens communications - customer with bad D&B score requesting grace period
  {
    id: 'comm-walgreens-001',
    customerId: 'walgreens',
    type: 'email',
    direction: 'inbound',
    date: '2026-01-21T08:30:00',
    subject: 'Re: Past Due Notice - Request for Payment Extension',
    details: 'Customer requesting 60-day grace period',
    response: null,
    classifications: ['Grace Period Request', 'Payment Discussion'],
    sentiment: 'neutral',
    summary:
      'Walgreens AP requesting 60-day payment extension on $2.65M overdue balance citing cash flow challenges.',
    emailBody: `Hi,

Thank you for your patience. We're currently facing some short-term cash flow challenges due to unexpected inventory write-offs from our Q4 restructuring.

We would like to request a 60-day grace period on our outstanding balance of $2.65M. We're committed to our partnership and will resume regular payments by the end of March.

Would you be open to discussing this arrangement? We can also offer to pay interest on the extended amount if that helps.

Please let me know your thoughts.

Best regards,
Karen Mitchell
Director of Accounts Payable
Walgreens Boots Alliance`,
    pendingActions: [
      {
        id: 'action-grace-approve',
        type: 'approve_grace_period',
        label: 'Approve Grace Period',
        description: 'Grant 60-day extension as requested',
      },
      {
        id: 'action-escalate',
        type: 'escalate',
        label: 'Escalate to Collections',
        description: 'Escalate for immediate collection action',
        aiRecommended: true,
        aiReason:
          'D&B profile shows High failure risk (score 7/9) and PAYDEX of 52 indicates poor payment history. Recommending escalation to protect exposure.',
      },
    ],
  },
  {
    id: 'comm-walgreens-002',
    customerId: 'walgreens',
    type: 'email',
    direction: 'outbound',
    date: '2026-01-18T10:00:00',
    subject: 'Past Due Notice - Immediate Attention Required',
    details: 'Formal past due notice sent',
    response: 'Received',
    classifications: ['Past Due Notice'],
    sentiment: null,
    summary:
      'Formal notice for $2.65M overdue with warning about potential credit hold.',
    emailBody: `Dear Karen,

This is a formal notice regarding your account with an overdue balance of $2,650,000.

Your account is now 30+ days past due on multiple invoices. If payment or a payment arrangement is not received within 10 business days, we may need to place a hold on future shipments.

Please contact us immediately to discuss resolution options.

Regards,
Financial Team`,
  },
  {
    id: 'comm-walgreens-003',
    customerId: 'walgreens',
    type: 'phone',
    direction: 'outbound',
    date: '2026-01-15T14:00:00',
    subject: 'Payment status call',
    details: 'Left voicemail for AP team',
    response: 'No callback',
    classifications: ['Payment Inquiry'],
    sentiment: null,
    summary:
      'Attempted to reach Walgreens AP. Left voicemail requesting callback regarding overdue invoices.',
  },
  {
    id: 'comm-walgreens-004',
    customerId: 'walgreens',
    type: 'email',
    direction: 'outbound',
    date: '2026-01-10T09:30:00',
    subject: 'January 2026 Statement - Walgreens Boots Alliance',
    details: 'Monthly statement sent',
    response: null,
    classifications: ['Statement'],
    sentiment: null,
    summary:
      'Monthly statement showing $6.1M total outstanding with $2.65M overdue.',
  },
];

export const MOCK_SCHEDULED_COMMUNICATIONS: ScheduledCommunication[] = [
  {
    id: 'sched-001',
    customerId: 'walmart',
    scheduledDate: '2026-01-27',
    type: 'email',
    details: 'Follow up on $5.2M wire transfer confirmation',
  },
  {
    id: 'sched-002',
    customerId: 'walmart',
    scheduledDate: '2026-01-28',
    type: 'phone',
    details: 'Discuss remaining $7.2M balance timeline',
  },
  {
    id: 'sched-003',
    customerId: 'target',
    scheduledDate: '2026-01-23',
    type: 'email',
    details: 'Payment reminder for 32 overdue invoices ($8.95M)',
  },
  {
    id: 'sched-004',
    customerId: 'costco',
    scheduledDate: '2026-01-24',
    type: 'phone',
    details: 'Discuss pricing dispute resolution for $1.2M',
  },
];

// ============ AI Suggested Actions ============

export const MOCK_AI_SUGGESTED_ACTIONS: AISuggestedAction[] = [
  {
    id: 'action-001',
    customerId: 'walmart',
    type: 'review_response',
    title: 'Confirm wire transfer received',
    description:
      'Verify $5.2M wire transfer from Friday was received and applied correctly',
    priority: 'urgent',
    relatedInvoices: ['WM-2026-001', 'WM-2026-002', 'WM-2026-003'],
  },
  {
    id: 'action-002',
    customerId: 'walmart',
    type: 'follow_up',
    title: 'Follow up on remaining balance',
    description:
      'Customer committed to paying remaining $7.2M by month end (from 01/08 email)',
    priority: 'normal',
    relatedInvoices: ['WM-2026-004', 'WM-2026-005', 'WM-2026-006'],
  },
  {
    id: 'action-003',
    customerId: 'costco',
    type: 'review_response',
    title: 'Review pricing dispute',
    description:
      'Customer disputing $1.2M in charges - verify contracted rates for organic tomatoes',
    priority: 'urgent',
    relatedInvoices: ['CC-2025-089', 'CC-2025-090', 'CC-2025-091'],
  },
  {
    id: 'action-004',
    customerId: 'target',
    type: 'send_reminder',
    title: 'Send escalation email',
    description:
      'No response to payment inquiry sent 6 days ago for $8.95M overdue',
    priority: 'urgent',
    relatedInvoices: ['TG-2025-201', 'TG-2025-202'],
  },
  {
    id: 'action-005',
    customerId: 'kroger',
    type: 'send_reminder',
    title: 'Send payment reminder',
    description:
      'Automated reminder for 8 invoices totaling $2.1M due in 5 days',
    priority: 'normal',
    relatedInvoices: ['KR-2026-001', 'KR-2026-002'],
  },
];

// ============ Collection Tasks ============

export const MOCK_COLLECTION_TASKS: CollectionTask[] = [
  {
    id: 'task-001',
    customerId: 'walmart',
    title: 'Verify wire transfer',
    description: 'Confirm $5.2M wire received and applied to correct invoices',
    type: 'payment_promise',
    status: 'open',
    createdAt: '2026-01-20T14:30:00Z',
  },
  {
    id: 'task-002',
    customerId: 'walmart',
    title: 'Review payment plan',
    description: 'Document month-end commitment for remaining $7.2M balance',
    type: 'review',
    status: 'open',
    createdAt: '2026-01-20T14:35:00Z',
  },
  {
    id: 'task-003',
    customerId: 'costco',
    title: 'Resolve pricing dispute',
    description: 'Verify contracted rates and issue credit memo if applicable',
    type: 'dispute',
    status: 'open',
    createdAt: '2026-01-19T10:00:00Z',
  },
  {
    id: 'task-004',
    customerId: 'target',
    title: 'Escalate to management',
    description:
      'No response to multiple outreach attempts - escalate internally',
    type: 'action',
    status: 'completed',
    createdAt: '2026-01-18T09:00:00Z',
    completedAt: '2026-01-18T09:30:00Z',
  },
  {
    id: 'task-005',
    customerId: 'kroger',
    title: 'Initial outreach',
    description: 'First contact for upcoming payment due in 5 days',
    type: 'outreach',
    status: 'closed',
    createdAt: '2026-01-15T08:00:00Z',
    completedAt: '2026-01-15T08:45:00Z',
  },
  {
    id: 'task-006',
    customerId: 'costco',
    title: 'Request documentation',
    description: 'Get copies of original POs to verify pricing',
    type: 'update_contact',
    status: 'open',
    createdAt: '2026-01-19T09:20:00Z',
  },
];

// ============ AI Agent Runs ============

export const MOCK_AI_AGENT_RUNS: AIAgentRun[] = [
  {
    id: 'agent-run-001',
    customerId: 'target',
    type: 'dispute_submission',
    status: 'completed',
    startedAt: '2026-01-15T14:30:00Z',
    completedAt: '2026-01-15T14:31:15Z',
    description:
      'Submitted dispute for Invoice #TG-2025-4521 on Target vendor portal',
    result: 'Dispute submitted successfully. Case ID: TGT-DSP-2026-0892',
    portalUrl: 'https://bullseyesample.online/',
    videoUrl:
      'https://gentlyai-demo-data-2026.s3.us-east-2.amazonaws.com/recording.target_dispute.mp4',
    invoiceNumber: 'TG-2025-4521',
    amountDisputed: 2450000,
  },
];

export const MOCK_AI_AGENT_MONITORS: AIAgentMonitor[] = [
  {
    id: 'monitor-001',
    customerId: 'target',
    title: 'Dispute Status Monitor',
    description:
      'Checking Target vendor portal for response to dispute case TGT-DSP-2026-0892',
    status: 'active',
    checkFrequency: 'Daily at 9:00 AM EST',
    lastCheckAt: '2026-01-21T14:00:00Z',
    nextCheckAt: '2026-01-22T14:00:00Z',
    checksLog: [
      {
        timestamp: '2026-01-21T14:00:00Z',
        status: 'no_update',
        message: 'No response yet. Dispute still under review.',
      },
      {
        timestamp: '2026-01-20T14:00:00Z',
        status: 'no_update',
        message: 'No response yet. Dispute still under review.',
      },
      {
        timestamp: '2026-01-19T14:00:00Z',
        status: 'no_update',
        message: 'No response yet. Dispute still under review.',
      },
      {
        timestamp: '2026-01-18T14:00:00Z',
        status: 'no_update',
        message: 'No response yet. Dispute still under review.',
      },
      {
        timestamp: '2026-01-17T14:00:00Z',
        status: 'no_update',
        message: 'No response yet. Dispute status: Pending Review.',
      },
      {
        timestamp: '2026-01-16T14:00:00Z',
        status: 'no_update',
        message: 'Dispute acknowledged by Target AP team.',
      },
    ],
  },
];

// ============ Helper Functions ============

export function getAIAgentRunsForCustomer(customerId: string): AIAgentRun[] {
  return MOCK_AI_AGENT_RUNS.filter((r) => r.customerId === customerId);
}

export function getAIAgentMonitorsForCustomer(
  customerId: string,
): AIAgentMonitor[] {
  return MOCK_AI_AGENT_MONITORS.filter((m) => m.customerId === customerId);
}

export function getAIActionsForCustomer(
  customerId: string,
): AISuggestedAction[] {
  return MOCK_AI_SUGGESTED_ACTIONS.filter((a) => a.customerId === customerId);
}

export function getTasksForCustomer(customerId: string): CollectionTask[] {
  return MOCK_COLLECTION_TASKS.filter((t) => t.customerId === customerId);
}

// ============ Aging Report Helpers ============

export function calculateAgingTotals() {
  const totals = {
    current: 0,
    days30: 0,
    days60: 0,
    days90: 0,
    over90: 0,
    total: 0,
  };

  MOCK_CUSTOMERS.forEach((customer) => {
    totals.current += customer.aging.current;
    totals.days30 += customer.aging.days30;
    totals.days60 += customer.aging.days60;
    totals.days90 += customer.aging.days90;
    totals.over90 += customer.aging.over90;
  });

  totals.total =
    totals.current +
    totals.days30 +
    totals.days60 +
    totals.days90 +
    totals.over90;

  return totals;
}

export function getAgingPercentages() {
  const totals = calculateAgingTotals();
  return {
    current: ((totals.current / totals.total) * 100).toFixed(1),
    days30: ((totals.days30 / totals.total) * 100).toFixed(1),
    days60: ((totals.days60 / totals.total) * 100).toFixed(1),
    days90: ((totals.days90 / totals.total) * 100).toFixed(1),
    over90: ((totals.over90 / totals.total) * 100).toFixed(1),
  };
}

export function getCustomerById(customerId: string): ARCustomer | undefined {
  return MOCK_CUSTOMERS.find((c) => c.id === customerId);
}

export function getCommunicationsForCustomer(
  customerId: string,
): ARCommunication[] {
  return MOCK_COMMUNICATIONS.filter((c) => c.customerId === customerId);
}

export function getScheduledCommunicationsForCustomer(
  customerId: string,
): ScheduledCommunication[] {
  return MOCK_SCHEDULED_COMMUNICATIONS.filter(
    (c) => c.customerId === customerId,
  );
}

// ============ AR Overview Dashboard Data ============

export interface AROverviewSnapshot {
  openInvoices: number;
  outstandingBalance: number;
  overdueInvoices: number;
  overdueBalance: number;
}

export interface ARCollectionsMetrics {
  outreachSentReceived: number;
  collectedWithAI: number;
  outreachAutomated: number;
  totalCollected: number;
}

export interface ARAgingBucket {
  label: string;
  amount: number;
  percentage: number;
}

export interface ARTrendDataPoint {
  month: string;
  value: number;
}

export interface AROverviewData {
  // Summary cards
  totalOutstanding: number;
  totalOverdue: number;
  cashCollectedThisMonth: number;
  invoicesFollowedUpThisMonth: number;
  invoicesCollectedThisMonth: number;

  // AI Impact metrics
  collectedWithAI: number;
  aiCollectionRate: number;
  callsEmailsAutomated: number;

  // Snapshot
  snapshot: AROverviewSnapshot;

  // Collections (with period filters)
  collectionsToday: ARCollectionsMetrics;
  collectionsYTD: ARCollectionsMetrics;
  collectionsAll: ARCollectionsMetrics;

  // Aging buckets
  agingBuckets: ARAgingBucket[];

  // Trend data
  overdueBalanceTrend: ARTrendDataPoint[];
  cashCollectedTrend: ARTrendDataPoint[];
  outboundAutomationTrend: ARTrendDataPoint[];

  // Task summary
  taskSummary: {
    total: number;
    completed: number;
    inProgress: number;
    pending: number;
    failed: number;
  };
}

export const MOCK_AR_OVERVIEW: AROverviewData = {
  // Summary cards
  totalOutstanding: 102700000, // $102.7M
  totalOverdue: 51070000, // $51.07M
  cashCollectedThisMonth: 31500000, // $31.5M
  invoicesFollowedUpThisMonth: 4947,
  invoicesCollectedThisMonth: 1100,

  // AI Impact metrics
  collectedWithAI: 117104526.37,
  aiCollectionRate: 80.95,
  callsEmailsAutomated: 20062,

  // Snapshot
  snapshot: {
    openInvoices: 475,
    outstandingBalance: 24384283.1,
    overdueInvoices: 475,
    overdueBalance: 24384283.1,
  },

  // Collections - Today
  collectionsToday: {
    outreachSentReceived: 42,
    collectedWithAI: 156420.5,
    outreachAutomated: 18,
    totalCollected: 287650.25,
  },

  // Collections - YTD
  collectionsYTD: {
    outreachSentReceived: 727,
    collectedWithAI: 2646685.2,
    outreachAutomated: 143,
    totalCollected: 3262254.07,
  },

  // Collections - All time
  collectionsAll: {
    outreachSentReceived: 2845,
    collectedWithAI: 8942150.75,
    outreachAutomated: 892,
    totalCollected: 12456890.32,
  },

  // Aging buckets
  agingBuckets: [
    { label: 'Current', amount: 2500000, percentage: 6.2 },
    { label: '1-30 days', amount: 3800000, percentage: 9.4 },
    { label: '31-60 days', amount: 5200000, percentage: 12.9 },
    { label: '61-90 days', amount: 8700000, percentage: 21.6 },
    { label: '91+ days', amount: 20100000, percentage: 49.9 },
  ],

  // Trend data - Overdue Balance %
  overdueBalanceTrend: [
    { month: 'Apr', value: 92 },
    { month: 'May', value: 95 },
    { month: 'Jun', value: 88 },
    { month: 'Jul', value: 45 },
    { month: 'Aug', value: 32 },
    { month: 'Sep', value: 28 },
    { month: 'Oct', value: 25 },
  ],

  // Trend data - Cash Collected (in millions)
  cashCollectedTrend: [
    { month: 'Apr', value: 45 },
    { month: 'May', value: 62 },
    { month: 'Jun', value: 58 },
    { month: 'Jul', value: 42 },
    { month: 'Aug', value: 38 },
    { month: 'Sep', value: 35 },
    { month: 'Oct', value: 31.5 },
  ],

  // Trend data - Outbound Automation %
  outboundAutomationTrend: [
    { month: 'Apr', value: 65 },
    { month: 'May', value: 72 },
    { month: 'Jun', value: 68 },
    { month: 'Jul', value: 22 },
    { month: 'Aug', value: 70 },
    { month: 'Sep', value: 71 },
    { month: 'Oct', value: 69 },
  ],

  // Task summary
  taskSummary: {
    total: 13000,
    completed: 11200,
    inProgress: 850,
    pending: 750,
    failed: 200,
  },
};
